import { ArrowLeft, History, CreditCard } from 'lucide-react';
import React, { useEffect, useState, useRef } from 'react';
import { Asset, AssetHistory } from '../lib/types';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  storage,
  db,
  collection,
  doc,
  setDoc,
  addDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
} from '../firebase';
import { AssetDetailsForm } from './modals/AssetDetailsForm';
import { AssetHistoryTab } from './modals/AssetHistoryTab';
import { AssetExpensesTab } from './modals/AssetExpensesTab';

export interface AssetExpense {
  id: string;
  item: string;
  category: 'Initial Cost' | 'Maintenance';
  localCost: number;
  foreignCost: number;
  date: string;
  note?: string;
  receipt?: string;
  personInCharge: string;
}

interface AssetDetailScreenProps {
  asset: Asset | null;
  onBack: () => void;
  onAddAsset: (asset: Omit<Asset, 'id' | 'uid'>) => Promise<void>;
  onUpdateAsset: (id: string, asset: Partial<Asset>) => Promise<void>;
  currency: string;
  overseasCurrency: string;
}

export function AssetDetailScreen({
  asset,
  onBack,
  onAddAsset,
  onUpdateAsset,
  currency,
  overseasCurrency,
}: AssetDetailScreenProps) {
  const [formData, setFormData] = useState<Omit<Asset, 'id' | 'uid'>>({
    name: '',
    value: 0,
    type: 'Real Estate',
    location: '',
    picture: '',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingExpense, setIsAddingExpense] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const expenseReceiptRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const itemInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<
    'details' | 'history' | 'expenses'
  >('details');
  const [history, setHistory] = useState<AssetHistory[]>([]);
  const [expenses, setExpenses] = useState<AssetExpense[]>([]);
  const [newExpense, setNewExpense] = useState({
    item: '',
    category: 'Maintenance' as 'Initial Cost' | 'Maintenance',
    localCost: '',
    foreignCost: '',
    date: new Date().toISOString().split('T')[0],
    note: '',
    personInCharge: '',
  });
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);

  useEffect(() => {
    if (asset) {
      setFormData({
        name: asset.name,
        value: asset.value,
        type: asset.type,
        location: asset.location || '',
        picture: asset.picture || '',
      });
      setPreviewUrl(asset.picture || null);
      setActiveTab('details');

      const q = query(
        collection(db, 'assets', asset.id, 'history'),
        orderBy('date', 'asc')
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const historyData = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as AssetHistory
        );
        setHistory(historyData);
      });

      const expensesQ = query(
        collection(db, 'assets', asset.id, 'expenses'),
        orderBy('date', 'desc')
      );
      const unsubscribeExpenses = onSnapshot(expensesQ, (snapshot) => {
        const expensesData = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as AssetExpense
        );
        setExpenses(expensesData);
      });

      return () => {
        unsubscribe();
        unsubscribeExpenses();
      };
    }
  }, [asset]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.value || !formData.type) return;

    setIsSubmitting(true);
    try {
      let imageUrl = formData.picture;
      if (imageFile) {
        const storageRef = ref(
          storage,
          `assets/${asset?.uid || 'unknown'}/${Date.now()}_${imageFile.name}`
        );
        await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(storageRef);
      }

      const assetData = { ...formData, picture: imageUrl };

      if (asset) {
        await onUpdateAsset(asset.id, assetData);
      } else {
        await onAddAsset(assetData);
      }
      onBack();
    } catch (error) {
      console.error('Error saving asset:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!asset || !newExpense.item || !newExpense.localCost) return;

    setIsAddingExpense(true);
    try {
      let receiptUrl = '';
      if (expenseReceiptRef.current?.files?.[0]) {
        const file = expenseReceiptRef.current.files[0];
        const storageRef = ref(
          storage,
          `receipts/${asset.uid}/${Date.now()}_${file.name}`
        );
        await uploadBytes(storageRef, file);
        receiptUrl = await getDownloadURL(storageRef);
      }

      const expenseData = {
        ...newExpense,
        localCost: parseFloat(newExpense.localCost) || 0,
        foreignCost: parseFloat(newExpense.foreignCost) || 0,
        receipt:
          receiptUrl ||
          (editingExpenseId
            ? expenses.find((e) => e.id === editingExpenseId)?.receipt || ''
            : ''),
        uid: asset.uid,
      };

      if (editingExpenseId) {
        await setDoc(
          doc(db, 'assets', asset.id, 'expenses', editingExpenseId),
          expenseData
        );
      } else {
        await addDoc(
          collection(db, 'assets', asset.id, 'expenses'),
          expenseData
        );
      }

      setNewExpense({
        item: '',
        category: 'Maintenance',
        localCost: '',
        foreignCost: '',
        date: new Date().toISOString().split('T')[0],
        note: '',
        personInCharge: '',
      });
      setEditingExpenseId(null);
      setIsExpenseModalOpen(false);
      if (expenseReceiptRef.current) expenseReceiptRef.current.value = '';
    } catch (error) {
      console.error('Error adding expense:', error);
    } finally {
      setIsAddingExpense(false);
    }
  };

  const handleEditExpense = (expense: AssetExpense) => {
    setNewExpense({
      item: expense.item,
      category: expense.category,
      localCost: expense.localCost.toString(),
      foreignCost: expense.foreignCost.toString(),
      date: expense.date,
      note: expense.note || '',
      personInCharge: expense.personInCharge || '',
    });
    setEditingExpenseId(expense.id);
    setIsExpenseModalOpen(true);
  };

  const handleDeleteExpense = async (expenseId: string) => {
    if (!asset) return;
    if (confirm('Are you sure you want to delete this expense?')) {
      try {
        await deleteDoc(doc(db, 'assets', asset.id, 'expenses', expenseId));
      } catch (error) {
        console.error('Error deleting expense:', error);
      }
    }
  };

  return (
    <div className="mx-auto max-w-4xl pb-20">
      <div className="mb-8 flex items-center gap-4">
        <button
          onClick={onBack}
          className="rounded-xl border border-zinc-100 bg-white p-3 text-zinc-400 shadow-sm transition-colors hover:text-zinc-900 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:text-zinc-100"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
            {asset ? 'Edit Asset' : 'Add New Asset'}
          </h2>
          <p className="text-sm font-medium text-zinc-500">
            {asset ? asset.name : 'Track your physical and digital assets'}
          </p>
        </div>
      </div>

      {asset && (
        <div className="mb-8 flex gap-2 rounded-2xl bg-zinc-100 p-1.5 dark:bg-zinc-900">
          <button
            onClick={() => setActiveTab('details')}
            className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-bold transition-all ${
              activeTab === 'details'
                ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-100'
                : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-300'
            }`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all ${
              activeTab === 'history'
                ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-100'
                : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-300'
            }`}
          >
            <History className="h-4 w-4" />
            History
          </button>
          <button
            onClick={() => setActiveTab('expenses')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all ${
              activeTab === 'expenses'
                ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-100'
                : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-300'
            }`}
          >
            <CreditCard className="h-4 w-4" />
            Expenses
          </button>
        </div>
      )}

      <div ref={scrollContainerRef}>
        {activeTab === 'details' ? (
          <AssetDetailsForm
            formData={formData}
            setFormData={setFormData}
            imageFile={imageFile}
            setImageFile={setImageFile}
            previewUrl={previewUrl}
            setPreviewUrl={setPreviewUrl}
            isSubmitting={isSubmitting}
            handleSubmit={handleSubmit}
            fileInputRef={fileInputRef}
            editingAsset={asset}
          />
        ) : activeTab === 'history' ? (
          <AssetHistoryTab history={history} currency={currency} />
        ) : (
          <AssetExpensesTab
            expenses={expenses}
            newExpense={newExpense}
            setNewExpense={setNewExpense}
            isAddingExpense={isAddingExpense}
            handleAddExpense={handleAddExpense}
            handleEditExpense={handleEditExpense}
            editingExpenseId={editingExpenseId}
            handleDeleteExpense={handleDeleteExpense}
            expenseReceiptRef={expenseReceiptRef}
            itemInputRef={itemInputRef}
            currency={currency}
            overseasCurrency={overseasCurrency}
            isExpenseModalOpen={isExpenseModalOpen}
            setIsExpenseModalOpen={setIsExpenseModalOpen}
          />
        )}
      </div>
    </div>
  );
}
